import React, { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function toText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return String(value);
}

function normalizeSteps(steps) {
  return toArray(steps)
    .map((step, index) => {
      if (typeof step === "string") {
        return { title: step, details: "", index };
      }
      if (!step || typeof step !== "object") return null;
      return {
        index,
        title: toText(step.title || step.name || step.point || `Step ${index + 1}`),
        details: toText(step.details || step.description || step.content || step.rationale || ""),
      };
    })
    .filter(Boolean);
}

function normalizeTabs(payload) {
  const tabs = toArray(payload?.tabs || payload?.scenarios || payload?.comparisons || payload?.options || payload?.cases);
  if (tabs.length > 0) {
    return tabs
      .map((tab, index) => {
        if (!tab || typeof tab !== "object") return null;
        return {
          id: String(tab.id || index),
          label: toText(tab.label || tab.name || tab.scenario || tab.option || tab.case || `Track ${index + 1}`),
          objective: toText(tab.objective || tab.goal || tab.description || ""),
          steps: normalizeSteps(tab.steps || tab.items || tab.differences || tab.details || tab.points || []),
        };
      })
      .filter((tab) => tab && tab.steps.length > 0);
  }

  const fallbackSteps = normalizeSteps(payload?.steps || payload?.workflow || payload?.scenario || payload?.comparison || payload?.points || []);
  if (fallbackSteps.length === 0) return [];
  return [{ id: "default", label: "Workflow", objective: "", steps: fallbackSteps }];
}

export default function WorkflowCards({ payload }) {
  const tabs = useMemo(() => normalizeTabs(payload || {}), [payload]);
  const [activeTab, setActiveTab] = useState(0);

  if (tabs.length === 0) {
    return (
      <div style={{
        padding: "10px 14px",
        background: "var(--bg-secondary, #f5f5f5)",
        border: "1px solid var(--border-color, #ddd)",
        borderRadius: 8,
        color: "var(--text-secondary, #888)",
        fontSize: "0.85rem",
        fontStyle: "italic",
      }}>
        Visualization unavailable — the model did not produce a valid structure.
        Try a more capable model response.
      </div>
    );
  }

  const tab = tabs[Math.min(activeTab, tabs.length - 1)];
  const title = toText(payload?.title || payload?.workflow_title || payload?.scenario_title || "Overview");
  const context = toText(payload?.context || payload?.summary || payload?.overview || "");

  return (
    <div style={{ margin: "12px 0", border: "1px solid var(--border-color, #ddd)", borderRadius: 10, background: "var(--background-color, #fff)" }}>
      <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--border-color, #ddd)" }}>
        <div style={{ fontWeight: 700 }}>{title}</div>
        {context ? <div style={{ marginTop: 4, color: "var(--text-secondary, #666)", fontSize: "0.9rem" }}>{context}</div> : null}
      </div>

      {tabs.length > 1 && (
        <div style={{ display: "flex", gap: 8, padding: "10px 12px", borderBottom: "1px solid var(--border-color, #eee)", overflowX: "auto" }}>
          {tabs.map((item, idx) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveTab(idx)}
              style={{
                border: "1px solid var(--primary-color, #636cff)",
                background: idx === activeTab ? "var(--primary-color, #636cff)" : "transparent",
                color: idx === activeTab ? "#fff" : "var(--primary-color, #636cff)",
                borderRadius: 999,
                padding: "4px 10px",
                fontSize: "0.82rem",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ padding: "12px" }}>
        {tab.objective ? (
          <div style={{ marginBottom: 10, color: "var(--text-secondary, #666)", fontSize: "0.9rem" }}>
            {tab.objective}
          </div>
        ) : null}

        {tab.steps.map((step) => {
          return (
            <div key={`${tab.id}-${step.index}`} style={{ border: "1px solid var(--border-color, #e5e7eb)", borderRadius: 8, padding: "10px", marginBottom: 10 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{ fontWeight: 600 }}>{step.index + 1}. {step.title}</div>
              </div>
              {step.details ? (
                <div className="markdown-content" style={{ marginTop: 6, fontSize: "0.95rem", color: "var(--text-color, #333)" }}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeSanitize]}
                  >
                    {step.details}
                  </ReactMarkdown>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
